// Global cache for Intl.DateTimeFormat objects to improve performance, especially in tests
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(options);
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', options);
    formatterCache.set(key, formatter);
  }
  return formatter;
}

/**
 * Returns the local date in YYYY-MM-DD format
 * This uses the locale date methods to avoid timezone conversion issues
 * that happen when using toISOString() (which converts to UTC)
 */
export const getLocalDateString = (): string => {
  const d = new Date();
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
};

/**
 * Formats a timestamp into a "Saved ..." string according to specific business rules:
 * - < 1 minute ago: "Just now"
 * - < 1 hour ago: "X minutes ago"
 * - Today (>= 1 hour): "X hours ago"
 * - Yesterday: "Yesterday at h:mm A"
 * - This year: "MMM D, h:mm A"
 * - Previous years: "MMM D, YYYY, h:mm A"
 *
 * @param timestamp The timestamp to format (in ms)
 * @param now The current timestamp (defaults to Date.now(), useful for testing)
 * @returns Formatted string (without the "Saved " prefix)
 */
export function formatSavedTime(timestamp: number, now: number = Date.now()): string {
  const date = new Date(timestamp);
  const nowDate = new Date(now);
  const diffMs = now - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);

  // Check if it's the same calendar day
  const isSameDay =
    date.getDate() === nowDate.getDate() &&
    date.getMonth() === nowDate.getMonth() &&
    date.getFullYear() === nowDate.getFullYear();

  // Check if it's yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  // Time formatter (e.g. 2:33 PM)
  const timeFormatter = getFormatter({
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (isSameDay) {
    if (diffMins < 1) {
      return 'Just now';
    }
    if (diffMins < 60) {
      return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    }
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }

  if (isYesterday) {
    return `Yesterday at ${timeFormatter.format(date)}`;
  }

  // Date formatter
  const isSameYear = date.getFullYear() === nowDate.getFullYear();

  // Options for "MMM D" or "MMM D, YYYY"
  const dateFormatter = getFormatter({
    month: 'short',
    day: 'numeric',
    year: isSameYear ? undefined : 'numeric',
  });

  return `${dateFormatter.format(date)}, ${timeFormatter.format(date)}`;
}

/**
 * Formats a timestamp into a relative time string:
 * - < 1 minute: "Just now"
 * - < 1 hour: "X minutes ago"
 * - < 24 hours: "X hours ago"
 * - Yesterday: "Yesterday"
 * - < 7 days: "X days ago"
 * - 7-13 days: "Last week"
 * - 14-29 days: "X weeks ago"
 * - 30-59 days: "1 month ago"
 * - 60-364 days: "X months ago"
 * - >= 365 days: "X years ago"
 *
 * @param timestamp The timestamp to format (in ms)
 * @param now The current timestamp (defaults to Date.now())
 * @returns {string} Formatted relative time
 */
export function formatRelativeTime(timestamp: number, now: number = Date.now()): string {
  const date = new Date(timestamp);
  const diffMs = now - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) {
    return 'Just now';
  }
  if (diffMins < 60) {
    return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }

  // Check if it's technically "Yesterday" calendar-wise
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isYesterday) {
    return 'Yesterday';
  }

  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }
  if (diffDays < 14) {
    return 'Last week';
  }
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} weeks ago`;
  }
  if (diffDays < 60) {
    return '1 month ago';
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }

  const years = Math.floor(diffDays / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

/**
 * Formats a timestamp into a scheduled time string:
 * - Today: "Today at h:mm A"
 * - Tomorrow: "Tomorrow at h:mm A"
 * - This year: "MMM D at h:mm A"
 * - Future years: "MMM D, YYYY at h:mm A"
 *
 * @param timestamp The timestamp to format
 * @returns Formatted string
 */
export interface ScheduledTimeFormatOptions {
  now?: Date;
  timeZone?: string;
}

function getCalendarDateParts(date: Date, timeZone?: string) {
  const parts = getFormatter({
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).formatToParts(date);
  const readPart = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);

  return {
    year: readPart('year'),
    month: readPart('month'),
    day: readPart('day'),
  };
}

function getCalendarDayNumber(date: Date, timeZone?: string): number {
  const { year, month, day } = getCalendarDateParts(date, timeZone);
  return Date.UTC(year, month - 1, day) / 86_400_000;
}

export function formatScheduledTime(
  timestamp: string | number | Date,
  { now = new Date(), timeZone }: ScheduledTimeFormatOptions = {}
): string {
  const date = new Date(timestamp);
  const targetDay = getCalendarDayNumber(date, timeZone);
  const currentDay = getCalendarDayNumber(now, timeZone);
  const isSameDay = targetDay === currentDay;
  const isTomorrow = targetDay === currentDay + 1;

  // Time formatter (e.g. 2:33 PM)
  const timeFormatter = getFormatter({
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  });

  const timePart = timeFormatter.format(date);

  if (isSameDay) {
    return `Today at ${timePart}`;
  }

  if (isTomorrow) {
    return `Tomorrow at ${timePart}`;
  }

  // Date formatter
  const isSameYear =
    getCalendarDateParts(date, timeZone).year === getCalendarDateParts(now, timeZone).year;

  // Options for "MMM D" or "MMM D, YYYY"
  const dateFormatter = getFormatter({
    month: 'short',
    day: 'numeric',
    year: isSameYear ? undefined : 'numeric',
    timeZone,
  });

  return `${dateFormatter.format(date)} at ${timePart}`;
}

// Add a helper for general-purpose cached formatters
export function getCachedFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return getFormatter(options);
}
